/**
 * Image Cache Service
 *
 * Caches S3 presigned URL images as Blob URLs in memory.
 * Uses LRU eviction when cache exceeds max size.
 * Prevents redundant fetches for the same image.
 */

interface CacheEntry {
  blobUrl: string;
  lastAccessed: number;
  size: number; // approximate blob size in bytes
}

class ImageCacheService {
  private cache: Map<number, CacheEntry> = new Map();
  private pendingFetches: Map<number, Promise<string>> = new Map();
  private maxEntries: number;
  private totalSizeLimit: number; // bytes
  private currentTotalSize: number = 0; // track total size for efficiency

  constructor(maxEntries = 100, totalSizeLimitMB = 200) {
    this.maxEntries = maxEntries;
    this.totalSizeLimit = totalSizeLimitMB * 1024 * 1024;
  }

  /**
   * Get a cached blob URL for the given image ID.
   * Returns null if not cached.
   */
  get(imageId: number): string | null {
    const entry = this.cache.get(imageId);
    if (entry) {
      entry.lastAccessed = Date.now();
      return entry.blobUrl;
    }
    return null;
  }

  /**
   * Check if an image is currently cached.
   */
  has(imageId: number): boolean {
    return this.cache.has(imageId);
  }

  /**
   * Fetch an image from the given URL, cache it as a Blob URL,
   * and return the Blob URL. Deduplicates concurrent fetches.
   */
  async fetchAndCache(imageId: number, url: string): Promise<string> {
    // Return cached if available
    const cached = this.get(imageId);
    if (cached) return cached;

    // Deduplicate concurrent fetches for the same image
    const pending = this.pendingFetches.get(imageId);
    if (pending) return pending;

    // Create promise with proper cleanup on both success and error
    const fetchPromise = this._doFetch(imageId, url).finally(() => {
      this.pendingFetches.delete(imageId);
    });

    this.pendingFetches.set(imageId, fetchPromise);
    return fetchPromise;
  }

  private async _doFetch(imageId: number, url: string): Promise<string> {
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    // Evict if needed before adding
    this._evictIfNeeded(blob.size);

    this.cache.set(imageId, {
      blobUrl,
      lastAccessed: Date.now(),
      size: blob.size,
    });
    this.currentTotalSize += blob.size;

    return blobUrl;
  }

  /**
   * Evict least recently used entries until there's room.
   */
  private _evictIfNeeded(incomingSize: number): void {
    // Evict by count
    while (this.cache.size >= this.maxEntries) {
      this._evictLRU();
    }

    // Evict by total size (using tracked total for efficiency)
    while (this.currentTotalSize + incomingSize > this.totalSizeLimit && this.cache.size > 0) {
      this._evictLRU();
    }
  }

  private _evictLRU(): void {
    let oldestKey: number | null = null;
    let oldestTime = Infinity;

    this.cache.forEach((entry, key) => {
      if (entry.lastAccessed < oldestTime) {
        oldestTime = entry.lastAccessed;
        oldestKey = key;
      }
    });

    if (oldestKey !== null) {
      const entry = this.cache.get(oldestKey);
      if (entry) {
        URL.revokeObjectURL(entry.blobUrl);
        this.currentTotalSize -= entry.size;
      }
      this.cache.delete(oldestKey);
    }
  }

  /**
   * Remove a specific image from cache.
   */
  remove(imageId: number): void {
    const entry = this.cache.get(imageId);
    if (entry) {
      URL.revokeObjectURL(entry.blobUrl);
      this.currentTotalSize -= entry.size;
      this.cache.delete(imageId);
    }
  }

  /**
   * Clear the entire cache, revoking all blob URLs.
   */
  clear(): void {
    this.cache.forEach((entry) => {
      URL.revokeObjectURL(entry.blobUrl);
    });
    this.cache.clear();
    this.pendingFetches.clear();
    this.currentTotalSize = 0;
  }

  /**
   * Get cache statistics.
   */
  getStats(): { entries: number; totalSizeMB: number } {
    return {
      entries: this.cache.size,
      totalSizeMB: this.currentTotalSize / (1024 * 1024),
    };
  }
}

// Singleton instance
const imageCacheService = new ImageCacheService(100, 200);

export default imageCacheService;
